import { LEVEL_ERROR, Provider as RollbarProvider } from '@rollbar/react'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import i18n from 'publisher/i18n-client'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { useSSR } from 'react-i18next'
import { Level } from 'rollbar'
import { createWebsitePublicPageTypeContent } from 'src/public-pages/pages/create-website-public-page-form-content'
import { PublicPageLayout } from 'src/public-pages/shared/components/layout/public-page-layout'
import { initPublisherSentry } from 'src/publisher/utils/init-sentry'
import { WebsiteRenderDataDto } from 'src/server/dto/website-render-data.dto'
import { WebsitePublicPageType } from 'src/shared/enums/website-public-page-form-type'

export const rollbarConfig = (reportLevel: Level) => ({
  enabled: process.env.NODE_ENV !== 'development',
  environment: process.env.APP_ENV,
  accessToken: process.env.ROLLBAR_PUBLISHER_KEY,
  captureUncaught: false,
  captureUnhandledRejections: false,
  autoInstrument: {
    network: false,
    log: false,
  },
  payload: {
    host: process.env.SYSTEME_IO_HOST,
  },
  reportLevel,
})

initPublisherSentry()

declare global {
  interface Window {
    __PUBLIC_PAGE_DATA__: {
      pageType: WebsitePublicPageType
      logoUrl?: string
      isCustomDomain?: boolean
      homePageUrl?: string
      hasWebsite?: boolean
      stylesAndTheme: WebsiteRenderDataDto
      locale?: PageLocaleEnum
      token?: string
    }
  }
}

interface WebsitePublicPageAppProps {
  rollbarReportLevel: Level
}

const App = ({ rollbarReportLevel }: WebsitePublicPageAppProps) => {
  const logoUrl = window.__PUBLIC_PAGE_DATA__.logoUrl
  const isCustomDomain = window.__PUBLIC_PAGE_DATA__.isCustomDomain
  const locale = window.__PUBLIC_PAGE_DATA__.locale
  const homePageUrl = window.__PUBLIC_PAGE_DATA__.homePageUrl
  const hasWebsite = window.__PUBLIC_PAGE_DATA__.hasWebsite
  const stylesAndTheme = window.__PUBLIC_PAGE_DATA__.stylesAndTheme
  const pageType = window.__PUBLIC_PAGE_DATA__.pageType
  const token = window.__PUBLIC_PAGE_DATA__.token

  const pageContent = createWebsitePublicPageTypeContent({
    pageType,
    homePageUrl,
    logoUrl,
    isCustomDomain,
    theme: stylesAndTheme.theme,
    heading: stylesAndTheme.style.typography?.heading,
    paragraph: stylesAndTheme.style.typography?.paragraph,
    button: stylesAndTheme.style.buttonsAndLinks?.primary,
    formFields: stylesAndTheme.style.formFields,
    linkButton: stylesAndTheme.style.buttonsAndLinks?.links,
    token,
    locale,
  })

  // @ts-expect-error: using the same way as in publisher/entrypoints/base-app.tsx
  useSSR(window.initialI18nStore, window.initialLanguage, { i18n })
  i18n.setDefaultNamespace('publicPages')

  return (
    <RollbarProvider config={rollbarConfig(rollbarReportLevel)}>
      <PublicPageLayout
        pageBackgroundColor={
          stylesAndTheme.theme.background.pageBackgroundColor
        }
        contentBackgroundColor={
          stylesAndTheme.theme.background.contentBackgroundColor
        }
        logoUrl={logoUrl}
        isCustomDomain={isCustomDomain}
        locale={locale}
        pageType={pageType}
        hasWebsite={hasWebsite}
        stylesAndTheme={stylesAndTheme}
      >
        {pageContent}
      </PublicPageLayout>
    </RollbarProvider>
  )
}

const appElement = document.getElementById('app')

if (appElement) {
  const root = ReactDOM.createRoot(appElement)
  root.render(<App rollbarReportLevel={LEVEL_ERROR} />)
}
